import React, { ComponentProps, useEffect, ReactElement } from 'react';

import PropTypes from 'prop-types';

import { DateRangePicker } from '@adsk/alloy-react-date-picker';
import FormField from '@adsk/alloy-react-form-field';
import {
  noop,
  optionalPropType,
  useForwardedRef,
} from '@adsk/alloy-react-helpers';
import { useLocale } from '@adsk/alloy-react-locale';
import Stepper from '@adsk/alloy-react-stepper';
import theme, { StyleProp } from '@adsk/alloy-react-theme';
import { format as formatTimeZone } from '@adsk/date-fns-tz';

import {
  DAYS_OF_THE_WEEK_IN_ENGLISH,
  INTERVAL_UNITS,
  DEFAULT_INTERVAL_UNITS,
} from './helpers/consts';
import { leftPositionedDropdown } from './helpers/styles';
import useScheduleData from './hooks/useScheduleData';
import useScheduleInitialState from './hooks/useScheduleInitialState';
import useScheduleState from './hooks/useScheduleState';
import MonthlyDropdown from './layout/MonthlyDropdown';
import MultiButtonGroup from './layout/MultiButtonGroup';
import ScheduleDropdown, {
  ScheduleDropdownProps,
} from './layout/ScheduleDropdown';
import SchedulePopover from './layout/SchedulePopover';
import { FormattedIntervalUnit, IntervalUnit, ScheduleData } from './types';

const defaultRenderDateRangePicker = (
  props: ComponentProps<typeof DateRangePicker>,
) => <DateRangePicker {...props} />;
const defaultRenderUnitSelectBox = (
  props: ScheduleDropdownProps<FormattedIntervalUnit>,
) => <ScheduleDropdown {...props} />;
const defaultRenderMonthlySelectBox = (
  props: ComponentProps<typeof MonthlyDropdown>,
) => <MonthlyDropdown {...props} />;
const defaultRenderRunAtSelectBox = (
  props: ScheduleDropdownProps<{
    label: string;
    value: string;
  }>,
) => <ScheduleDropdown {...props} />;
const defaultRenderStepper = (props: ComponentProps<typeof Stepper>) => (
  <Stepper {...props} />
);

type ScheduleProps = {
  data?: ScheduleData;
  units?: IntervalUnit[];
  disableWeeklyMultiDay?: boolean;
  hideDatePicker?: boolean;
  hideStepper?: boolean;
  label?: string;
  maxEndDate?: Date;
  runAtTooltip?: string;
  runAtTimeZone?: string;
  onChange?: (data: ScheduleData) => void;
  renderDateRangePicker?: (
    props: ComponentProps<typeof DateRangePicker>,
  ) => ReactElement;
  renderUnitSelectBox?: (
    props: ScheduleDropdownProps<FormattedIntervalUnit>,
  ) => ReactElement;
  renderMonthlySelectBox?: ({
    initialValue,
    onChange,
  }: {
    initialValue: number;
    onChange: (val: number) => void;
  }) => ReactElement;
  renderRunAtSelectBox?: (
    props: ScheduleDropdownProps<{
      label: string;
      value: string;
    }>,
  ) => ReactElement;
  renderStepper?: ({
    style,
    onChange,
    disabled,
    value,
    min,
    max,
  }: {
    style: StyleProp;
    onChange: (newNum?: number) => void;
    disabled: boolean;
    value: number;
    min: number;
    max: number;
  }) => ReactElement;
};
/**
 * A Schedule component that allows exposing/storing scheduling data.
 * Used for Reports & Data Connector.
 */
const Schedule = React.forwardRef<HTMLDivElement, ScheduleProps>(
  (
    {
      data = {},
      disableWeeklyMultiDay = false,
      hideDatePicker = false,
      hideStepper = false,
      label,
      maxEndDate,
      units = DEFAULT_INTERVAL_UNITS,
      runAtTooltip,
      runAtTimeZone,
      onChange = noop,
      renderDateRangePicker = defaultRenderDateRangePicker,
      renderUnitSelectBox = defaultRenderUnitSelectBox,
      renderMonthlySelectBox = defaultRenderMonthlySelectBox,
      renderRunAtSelectBox = defaultRenderRunAtSelectBox,
      renderStepper = defaultRenderStepper,
    },
    ref,
  ) => {
    const { T } = useLocale(({ locale }) => {
      return require(`./i18n/${locale}.json`);
    });

    const {
      localeObject,
      defaultRunOn,
      todayDate,
      todayDateNumber,
      todayWeekDay,
      startOfWeek,
      runAtTimes,
      formattedIntervalUnits,
    } = useScheduleData({ units });

    const {
      initialRunOn,
      initialIntervalUnit,
      initialUnitCycle,
      initialRunAtTime,
      initialStartDate,
      initialEndDate,
    } = useScheduleInitialState({
      localeObject,
      todayDate,
      todayWeekDay,
      todayDateNumber,
      formattedIntervalUnits,
      runAtTimes,
      runOn: data.runOn,
      intervalUnit: data.unit,
      runAtTime: data.runAtTime,
      startDate: data.startDate,
      endDate: data.endDate,
      unitCycle: data.unitCycle,
      maxEndDate,
    });

    const {
      selectedUnit,
      setSelectedUnit,
      runOn,
      setRunOn,
      unitCycle,
      setUnitCycle,
      runAtTime,
      setRunAtTime,
      dates,
      setDates,
    } = useScheduleState({
      initialRunOn,
      initialIntervalUnit,
      initialUnitCycle,
      initialStartDate,
      initialEndDate,
    });

    const datePickerRef = useForwardedRef(ref);

    useEffect(() => {
      onChange({
        unit: selectedUnit.value,
        unitCycle: unitCycle.toString(),
        runOn,
        runAtTime: runAtTime?.value || initialRunAtTime?.value,
        startDate: dates[0],
        endDate: dates[1],
      });
    }, [
      selectedUnit,
      unitCycle,
      runOn,
      runAtTime?.value,
      onChange,
      dates,
      initialRunAtTime?.value,
    ]);

    const handleOnStepperChange = (newNum?: number) => {
      if (!newNum || newNum < 1) {
        setUnitCycle(1);
      } else if (newNum > 99) {
        setUnitCycle(99);
      } else {
        setUnitCycle(newNum);
      }
    };

    const handleUnitChange = (newUnit: FormattedIntervalUnit) => {
      setSelectedUnit(newUnit);
      setUnitCycle(1);

      if (newUnit.value === initialIntervalUnit.value) {
        setRunOn(initialRunOn);
      } else {
        setRunOn(defaultRunOn[newUnit.value as IntervalUnit]);
      }
    };

    const renderRunOnForm = () => {
      if (selectedUnit.value === INTERVAL_UNITS.WEEK) {
        return (
          <MultiButtonGroup
            disableWeeklyMultiDay={disableWeeklyMultiDay}
            initialSelectedDays={
              initialIntervalUnit.value === INTERVAL_UNITS.WEEK
                ? initialRunOn
                : defaultRunOn.WEEK
            }
            todayDate={todayDate}
            localeObject={localeObject}
            onChange={setRunOn}
          />
        );
      } else {
        return renderMonthlySelectBox({
          initialValue: (initialIntervalUnit.value === INTERVAL_UNITS.MONTH
            ? initialRunOn[0]
            : defaultRunOn.MONTH[0]) as number,
          onChange: (val) => setRunOn([val]),
        });
      }
    };

    const renderTimeZoneAbbr = () => {
      let currentZone;
      try {
        currentZone = formatTimeZone(todayDate, 'zzz', {
          timeZone: runAtTimeZone,
        });
      } catch {
        currentZone = T.translate('INVALID_TIMEZONE');
      }

      return (
        runAtTimeZone && (
          <span
            css={[
              {
                display: 'flex',
                alignItems: 'center',
                ...theme.typography.labelMedium,
                color: theme.tokens.colors.text.secondary.value,
                whiteSpace: 'nowrap',
              },
            ]}
          >
            {currentZone}
          </span>
        )
      );
    };

    return (
      <>
        <div
          css={{
            display: 'inline-flex',
            alignItems: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <FormField
            style={[leftPositionedDropdown, { marginBottom: theme.spacing.M }]}
            data-testid={'schedule-units'}
            label={label}
            labelVariant={FormField.LABEL_VARIANTS.TOP}
          >
            {renderUnitSelectBox({
              initialSelectedItem: initialIntervalUnit,
              onChange: handleUnitChange,
              items: formattedIntervalUnits,
            })}
          </FormField>
          <div
            style={{
              ...(hideStepper && { visibility: 'hidden' }),
              display: 'inline-flex',
              alignItems: 'center',
              marginBottom: theme.spacing.M,
            }}
          >
            <FormField label={T.translate('EVERY')}>
              {renderStepper({
                style: {
                  marginLeft: theme.spacing.XS,
                  marginRight: theme.spacing.XS,
                },
                disabled: hideStepper,
                onChange: handleOnStepperChange,
                value: unitCycle,
                min: 1,
                max: 99,
              })}
            </FormField>
            <span style={{ color: theme.tokens.colors.text.secondary.value }}>
              {T.translate(
                `${selectedUnit.value}.${unitCycle > 1 ? 'PLURAL' : 'SINGULAR'}`,
              )}
            </span>
          </div>
        </div>
        <div
          css={{
            display: 'inline-flex',
            flexWrap: 'wrap',
          }}
        >
          {selectedUnit.value !== INTERVAL_UNITS.DAY && (
            <FormField
              style={[
                leftPositionedDropdown,
                { marginBottom: theme.spacing.M },
              ]}
              id={'schedule-run-on'}
              data-testid="schedule-run-on"
              labelVariant={FormField.LABEL_VARIANTS.TOP}
              label={
                <SchedulePopover
                  label={
                    selectedUnit.value === INTERVAL_UNITS.WEEK
                      ? T.translate('RUN_ON')
                      : T.translate('RUN_ON_DAY')
                  }
                  popoverMessage={T.translate('POPOVER_MESSAGE')}
                  shouldDisplayPopover={
                    selectedUnit.value === INTERVAL_UNITS.WEEK &&
                    !!disableWeeklyMultiDay
                  }
                />
              }
            >
              {renderRunOnForm()}
            </FormField>
          )}
          <div
            css={[
              {
                display: 'inline-flex',
                maxWidth: 'fit-content',
              },
            ]}
          >
            <FormField
              style={{
                '> div > div': { display: 'inline-flex' },
                marginBottom: theme.spacing.M,
              }}
              data-testid={'schedule-run-at'}
              labelVariant={FormField.LABEL_VARIANTS.TOP}
              label={
                <SchedulePopover
                  label={T.translate('RUN_AT')}
                  popoverMessage={runAtTooltip}
                  shouldDisplayPopover={Boolean(runAtTooltip)}
                />
              }
            >
              {renderRunAtSelectBox({
                initialSelectedItem: initialRunAtTime,
                onChange: setRunAtTime,
                items: runAtTimes,
                style: {
                  width: '250px',
                  marginRight: '10px',
                },
              })}
              {renderTimeZoneAbbr()}
            </FormField>
          </div>
        </div>
        <div>
          {!hideDatePicker && (
            <FormField
              label={T.translate('START_AND_END_DATES')}
              labelVariant={FormField.LABEL_VARIANTS.TOP}
            >
              {renderDateRangePicker({
                container: datePickerRef,
                endDate: dates[1],
                firstDayOfWeek: startOfWeek,
                maxDate: maxEndDate,
                minDate: todayDate,
                onChange: ({ startDate, endDate }) =>
                  setDates([startDate, endDate]),
                startDate: dates[0],
                style: { width: '300px' },
              })}
            </FormField>
          )}
        </div>
      </>
    );
  },
);

Schedule.displayName = 'Schedule';

const unitPropTypes = PropTypes.oneOf(Object.values(INTERVAL_UNITS));

Schedule.propTypes = {
  /**
   * Initial schedule data
   */
  data: optionalPropType(
    PropTypes.shape({
      unit: optionalPropType(unitPropTypes),
      unitCycle: optionalPropType(PropTypes.string),
      runOn: optionalPropType(
        PropTypes.oneOfType([
          PropTypes.arrayOf(
            PropTypes.oneOf(DAYS_OF_THE_WEEK_IN_ENGLISH).isRequired,
          ),
          PropTypes.arrayOf(PropTypes.number.isRequired),
        ]),
      ),
      runAtTime: optionalPropType(PropTypes.string),
      startDate: optionalPropType(PropTypes.instanceOf(Date)),
      endDate: optionalPropType(PropTypes.instanceOf(Date)),
    }),
  ),
  /**
   * Whether or not users can select multiple days for a weekly request
   */
  disableWeeklyMultiDay: PropTypes.bool,
  /**
   * Hide the date picker component
   */
  hideDatePicker: PropTypes.bool,
  /**
   * Hide the stepper component (every X days/weeks/months)
   */
  hideStepper: PropTypes.bool,
  /**
   * Label to be used for first FormField
   */
  label: PropTypes.string,
  /**
   * Maximum end date available for schedule
   */
  maxEndDate: PropTypes.instanceOf(Date),
  /**
   * Callback that will be called with current form data every time there's an update.
   */
  onChange: PropTypes.func,
  /**
   * Timezone shown for "Run at" field
   */
  runAtTimeZone: PropTypes.string,
  /**
   * Text to display in Tooltip in "Run at" field, for explaining the timezone shown
   */
  runAtTooltip: PropTypes.string,
  /**
   * Which units should be shown as options
   */
  units: PropTypes.arrayOf(unitPropTypes.isRequired),
  /** Render custom date range picker content */
  renderDateRangePicker: PropTypes.func,
  /** Render custom unit dropdown content */
  renderUnitSelectBox: PropTypes.func,
  /** Render custom monthly dropdown content */
  renderMonthlySelectBox: PropTypes.func,
  /** Render custom run at dropdown content */
  renderRunAtSelectBox: PropTypes.func,
  /** Render custom stepper content */
  renderStepper: PropTypes.func,
};

export default Object.assign(Schedule, { UNITS: INTERVAL_UNITS });
